import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLocations } from '@/domain/events/useEvents';
import { buildSiteFilterOptions, type SiteFilterRegion } from '@/domain/library';
import { formatCount } from '@/domain/warehouse';
import { fetchWarehouseTabCounts, warehouseQueryKeys } from '@/data/warehouse';
import CardTabs, { type CardTab } from '../../components/common/CardTabs';
import NewTransferDialog from '../../components/transfers/NewTransferDialog';
import ScanToReceiveDialog from '../../components/transfers/ScanToReceiveDialog';
import NewSubrentalDialog, { type NewSubrentalMode } from '../../components/procurement/NewSubrentalDialog';
import type { MultiSelectOption } from '../../components/SearchableMultiSelect';
import useSessionStore from '../../state/session';
import { useUserPrefs } from '../../hooks/useUserPrefs';
import { useRoles } from '../../hooks/useRoles';
import useCompactToolbar from '../../hooks/useCompactToolbar';
import useLibraryPhoneLayout from '../../hooks/useLibraryPhoneLayout';
import TransfersTab from './library/TransfersTab';
import RequestsTab from './library/RequestsTab';
import PurchaseOrdersTab from './library/PurchaseOrdersTab';
import SubrentalsTab from './library/SubrentalsTab';
import { NewPurchaseOrderDialog, PackingSlipOrderPicker } from './purchaseOrders';
import {
  WAREHOUSE_TABS,
  parseWarehouseTab,
  warehouseRecordHref,
  type WarehouseTabKey,
} from './library/warehouseTabs';
import type { WarehouseTabContext } from './library/shared';
import './library/WarehouseLibrary.css';

const TAB_ID_PREFIX = 'warehouse-tab';
const PANEL_ID = 'warehouse-tab-panel';
const tabElementId = (key: WarehouseTabKey) => `${TAB_ID_PREFIX}-${key}-tab`;

/**
 * Warehouse library (`/transfers`): Transfers, Requests, Purchase orders and Subrentals in one
 * Events-style library. Every row opens its record page. See docs/warehouse.md.
 */
export default function TransferOrdersPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canViewFeature, canEditFeature } = useRoles();
  const { get, set, flush, loaded: prefsLoaded } = useUserPrefs();
  const prefs = React.useMemo(() => ({ get, set, flush }), [flush, get, set]);
  const profileId = useSessionStore((state) => state.profileId);
  const assignedRegions = useSessionStore((state) => state.assignedRegions);
  const isPhone = useLibraryPhoneLayout();
  const compactToolbar = useCompactToolbar();
  const [createTransferOpen, setCreateTransferOpen] = React.useState(false);
  const [scanToReceiveOpen, setScanToReceiveOpen] = React.useState(false);
  const [newPurchaseOrderOpen, setNewPurchaseOrderOpen] = React.useState(false);
  const [slipPickerOpen, setSlipPickerOpen] = React.useState(false);
  const [newSubrental, setNewSubrental] = React.useState<{ mode: NewSubrentalMode; itemId: string | null } | null>(null);
  const openScanToReceive = React.useCallback(() => setScanToReceiveOpen(true), []);
  const closeScanToReceive = React.useCallback(() => setScanToReceiveOpen(false), []);
  const canEditPurchaseOrders = canEditFeature('maintenance.purchase_orders');
  const canEditSubrentals = canEditFeature('maintenance.xrental_orders');

  const visibleTabs = React.useMemo(
    () => WAREHOUSE_TABS.filter((tab) => !tab.featureKey || canViewFeature(tab.featureKey)),
    [canViewFeature],
  );
  const request = React.useMemo(() => parseWarehouseTab(searchParams.get('tab')), [searchParams]);
  const activeTab: WarehouseTabKey = visibleTabs.some((tab) => tab.key === request.tab) ? request.tab : 'transfers';
  // A legacy link's direction or status preset applies once, on arrival; the Transfers tab
  // clears it after reading it, so returning to the tab keeps the viewer's own filters.
  const [legacyTransfers, setLegacyTransfers] = React.useState(() => (
    request.tab === 'transfers' && (request.direction || request.statuses)
      ? { direction: request.direction, statuses: request.statuses }
      : null
  ));
  const clearLegacyTransfers = React.useCallback(() => setLegacyTransfers(null), []);

  // Old tab values and tabs this viewer can't open are rewritten to the canonical URL.
  React.useEffect(() => {
    const raw = searchParams.get('tab');
    if (raw === activeTab) return;
    if (!raw && activeTab === 'transfers') return;
    const next = new URLSearchParams(searchParams);
    next.set('tab', activeTab);
    setSearchParams(next, { replace: true });
  }, [activeTab, searchParams, setSearchParams]);

  // Each tab draws its own copy of the strip, so switching remounts it; keyboard focus that was on
  // the strip moves to the newly selected tab instead of falling back to the page.
  const refocusTabRef = React.useRef<WarehouseTabKey | null>(null);
  const selectTab = React.useCallback((key: WarehouseTabKey) => {
    const focused = document.activeElement;
    refocusTabRef.current = focused instanceof HTMLElement && focused.closest('.warehouse-tabs') ? key : null;
    const next = new URLSearchParams(searchParams);
    next.set('tab', key);
    setSearchParams(next);
  }, [searchParams, setSearchParams]);
  React.useEffect(() => {
    if (refocusTabRef.current !== activeTab) return;
    refocusTabRef.current = null;
    document.getElementById(tabElementId(activeTab))?.focus({ preventScroll: true });
  }, [activeTab]);

  const linkedEventId = searchParams.get('eventId')?.trim() || null;
  const linkedEvent = React.useMemo(
    () => (linkedEventId ? { id: linkedEventId, code: searchParams.get('eventCode')?.trim() || null } : null),
    [linkedEventId, searchParams],
  );
  const clearLinkedEvent = React.useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('eventId');
    next.delete('eventCode');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // `?new=equipment|vehicle&itemId=` (the item editor's "rent from a vendor" and old Vendor Rental
  // links) opens New subrental once, then leaves the URL.
  React.useEffect(() => {
    const mode = searchParams.get('new');
    if (mode !== 'equipment' && mode !== 'vehicle') return;
    if (canEditSubrentals) setNewSubrental({ mode, itemId: searchParams.get('itemId')?.trim() || null });
    const next = new URLSearchParams(searchParams);
    ['new', 'itemId', 'itemCode', 'itemName'].forEach((key) => next.delete(key));
    setSearchParams(next, { replace: true });
  }, [canEditSubrentals, searchParams, setSearchParams]);

  const countsQuery = useQuery({
    queryKey: warehouseQueryKeys.tabCounts(null),
    queryFn: () => fetchWarehouseTabCounts(null),
    staleTime: 60_000,
  });
  const counts = countsQuery.data;
  const tabs = React.useMemo<CardTab<WarehouseTabKey>[]>(() => visibleTabs.map((tab) => {
    const count = tab.key === 'transfers'
      ? counts?.transfers
      : tab.key === 'requests'
        ? counts?.requests
        : tab.key === 'purchase_orders'
          ? counts?.purchaseOrders
          : counts?.subrentals;
    return {
      key: tab.key,
      label: isPhone ? tab.shortLabel : tab.label,
      countLabel: count != null ? formatCount(count) : null,
    };
  }), [counts, isPhone, visibleTabs]);

  const locationsQuery = useLocations({ allowedLocationIds: null });
  const locations = React.useMemo(() => locationsQuery.data ?? [], [locationsQuery.data]);
  // Regions the viewer has blanket access to can be picked as one site filter value.
  const regionFilterOptions = React.useMemo<SiteFilterRegion[]>(() => {
    if (!assignedRegions.length || !locations.length) return [];
    return assignedRegions
      .filter((region) => region.grant_all_locations && region.is_active && region.name)
      .map((region) => ({
        region_id: region.region_id,
        name: region.name ?? '',
        locationIds: locations
          .filter((location) => location.region_id === region.region_id)
          .map((location) => location.location_id),
      }))
      .filter((region) => region.locationIds.length > 0)
      .sort((left, right) => left.name.localeCompare(right.name));
  }, [assignedRegions, locations]);
  const siteOptions = React.useMemo<MultiSelectOption[]>(
    () => buildSiteFilterOptions({
      regions: regionFilterOptions,
      sites: locations,
      canSelectAllSites: true,
      allLabel: 'All sites',
    }),
    [locations, regionFilterOptions],
  );
  const siteOptionIds = React.useMemo<ReadonlySet<string> | null>(
    () => (locationsQuery.isSuccess ? new Set(siteOptions.map((option) => option.id)) : null),
    [locationsQuery.isSuccess, siteOptions],
  );

  const handleTransferCreated = React.useCallback(function handleTransferCreated(transferId: string) {
    setCreateTransferOpen(false);
    void queryClient.invalidateQueries({ queryKey: warehouseQueryKeys.all });
    navigate(warehouseRecordHref.transfer(transferId));
  }, [navigate, queryClient]);
  const handlePurchaseOrderCreated = React.useCallback(function handlePurchaseOrderCreated(created: { poId: string }) {
    setNewPurchaseOrderOpen(false);
    navigate(warehouseRecordHref.purchaseOrder(created.poId, 'lines'));
  }, [navigate]);
  const handleSlipOrderPicked = React.useCallback(function handleSlipOrderPicked(poId: string) {
    setSlipPickerOpen(false);
    navigate(`${warehouseRecordHref.purchaseOrder(poId, 'receiving')}?scan=1`);
  }, [navigate]);
  const handleSubrentalCreated = React.useCallback(function handleSubrentalCreated(recordId: string) {
    setNewSubrental(null);
    navigate(warehouseRecordHref.subrental(recordId, 'lines'));
  }, [navigate]);

  const tabStrip = (
    <CardTabs
      tabs={tabs}
      activeKey={activeTab}
      onChange={selectTab}
      ariaLabel="Warehouse lists"
      idPrefix={TAB_ID_PREFIX}
      panelId={PANEL_ID}
      fill={isPhone}
      className="warehouse-tabs"
    />
  );
  const context: WarehouseTabContext = {
    tabStrip,
    panelId: PANEL_ID,
    tabElementId,
    prefs,
    prefsLoaded,
    profileId,
    isPhone,
    compactToolbar,
    siteOptions,
    siteOptionIds,
    regionFilterOptions,
    linkedEvent,
    onClearLinkedEvent: clearLinkedEvent,
  };

  return (
    <div className="settings-shell settings-page library-page library-page--phone events-page warehouse-hub-page warehouse-library-page">
      {activeTab === 'transfers' ? (
        <TransfersTab
          key="transfers"
          context={context}
          legacy={legacyTransfers}
          onLegacyApplied={clearLegacyTransfers}
          onNewTransfer={() => setCreateTransferOpen(true)}
          onScanToReceive={openScanToReceive}
        />
      ) : null}
      {activeTab === 'requests' ? <RequestsTab key="requests" context={context} /> : null}
      {activeTab === 'purchase_orders' ? (
        <PurchaseOrdersTab
          key="purchase_orders"
          context={context}
          currentProfileId={profileId}
          canEdit={canEditPurchaseOrders}
          onNewPurchaseOrder={() => setNewPurchaseOrderOpen(true)}
          onScanPackingSlip={() => setSlipPickerOpen(true)}
        />
      ) : null}
      {activeTab === 'subrentals' ? (
        <SubrentalsTab
          key="subrentals"
          context={context}
          canEdit={canEditSubrentals}
          onNewSubrental={() => setNewSubrental({ mode: 'equipment', itemId: null })}
          onScanToReceive={openScanToReceive}
        />
      ) : null}

      <NewTransferDialog
        open={createTransferOpen}
        onClose={() => setCreateTransferOpen(false)}
        onCreated={handleTransferCreated}
      />
      <ScanToReceiveDialog open={scanToReceiveOpen} onClose={closeScanToReceive} locationIds={null} />
      {canEditPurchaseOrders ? (
        <>
          <NewPurchaseOrderDialog
            open={newPurchaseOrderOpen}
            onClose={() => setNewPurchaseOrderOpen(false)}
            onCreated={handlePurchaseOrderCreated}
          />
          <PackingSlipOrderPicker
            open={slipPickerOpen}
            onClose={() => setSlipPickerOpen(false)}
            onPick={handleSlipOrderPicked}
          />
        </>
      ) : null}
      {canEditSubrentals ? (
        <NewSubrentalDialog
          open={newSubrental != null}
          onClose={() => setNewSubrental(null)}
          initialMode={newSubrental?.mode}
          initialItemId={newSubrental?.itemId ?? null}
          onCreated={handleSubrentalCreated}
        />
      ) : null}
    </div>
  );
}
